import { useState, useRef, useCallback, useEffect, memo, useMemo } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import {
  FolderOpen,
  History,
  Type,
  Blend,
  Captions,
  ScrollText,
  Music,
  Check,
  Loader2,
  Sliders,
  Trash2,
  ChevronRight,
  ChevronLeft,
  Bold,
  Italic,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import { ActivityTimeline } from './ActivityTimeline'
import { AssetLibraryPanel } from './AssetLibraryPanel'
import { MusicPicker } from './MusicPicker'
import { ScriptPanel } from './script-editor/ScriptPanel'
import { CaptionStylePanel } from './CaptionStylePanel'
import { FiltersPanel } from './FiltersPanel'
import { MotionPresetPicker } from './MotionPresetPicker'
import { KeyframeCurveEditor } from './KeyframeCurveEditor'
import {
  addKeyframe as addKf,
  removeKeyframe as removeKf,
  getKeyframeAtTime as getKfAtTime,
  updateKeyframe as updateKf,
} from '@/lib/keyframes'
import { buildTimelineModel, type TimelineMode } from '@/lib/clipUtils'
import { Id } from '../../../convex/_generated/dataModel'
import {
  TEXT_PRESETS,
  PRESET_CATEGORY_LABELS,
  PRESET_FONTS,
  resolveFontCSS,
  presetToOverlayFields,
  type TextPresetConfig,
  type TextPresetCategory,
} from '@/lib/textPresets'
import { TRANSITION_CATEGORIES, TransitionPreviewCanvas } from './TransitionPicker'
import { PillSlider } from '@/components/ui/pill-slider'

export type RightSidebarTab = 'script' | 'media' | 'music' | 'activity' | 'text' | 'transitions' | 'captions' | 'effects'

const ALL_TABS: { id: RightSidebarTab; icon: typeof FolderOpen; label: string }[] = [
  { id: 'script', icon: ScrollText, label: 'Script' },
  { id: 'media', icon: FolderOpen, label: 'Assets' },
  { id: 'music', icon: Music, label: 'Music' },
  { id: 'effects', icon: Sliders, label: 'Effects' },
  { id: 'text', icon: Type, label: 'Text' },
  { id: 'transitions', icon: Blend, label: 'Transitions' },
  { id: 'captions', icon: Captions, label: 'Captions' },
  { id: 'activity', icon: History, label: 'History' },
]

const CORE_TABS: Set<RightSidebarTab> = new Set(['script', 'media', 'activity'])

const SKILL_TAB_MAP: Record<string, RightSidebarTab[]> = {
  'voiceover': ['captions', 'music', 'script'],
  'dialog-editing': ['captions', 'script'],
  'narrative-first': ['captions', 'music', 'script', 'text'],
  'podcast-editing': ['captions', 'music'],
  'cinematic': ['effects', 'transitions', 'text', 'music'],
  'documentary': ['effects', 'transitions', 'text', 'music'],
  'music-video': ['effects', 'transitions', 'text', 'music'],
  'commercial': ['effects', 'transitions', 'text', 'music'],
  'social-short': ['effects', 'text', 'captions'],
  'anime': ['effects', 'transitions', 'text'],
}

function getVisibleTabs(activeSkillNames: string[]): typeof ALL_TABS {
  if (activeSkillNames.length === 0) return ALL_TABS
  const visible = new Set<RightSidebarTab>(CORE_TABS)
  for (const skill of activeSkillNames) {
    const tabs = SKILL_TAB_MAP[skill]
    if (tabs) tabs.forEach(t => visible.add(t))
  }
  return ALL_TABS.filter(t => visible.has(t.id))
}

interface RightSidebarProps {
  workflowId: Id<'workflows'>
  messages: any[]
  clips: any[]
  characters?: any[]
  isAdmin?: boolean
  selectedClipIndex: number | null
  currentTime: number
  textOverlays?: any[]
  selectedOverlayId?: string | null
  onOverlaySelect?: (overlayId: string | null) => void
  onOverlayUpdate?: (overlayId: string, updates: Record<string, any>) => void
  transitionPickerClipId?: string | null
  onTransitionPickerChange?: (clipId: string | null) => void
  projectTtsStatus?: string
  projectTtsTimestamps?: Array<{ word: string; startMs: number; endMs: number; inputIndex: number }>
  layoutActiveTab?: string | null
  timelineMode?: TimelineMode
}


const CAPTION_STYLES = [
  { id: 'bold', name: 'Bold', desc: 'White on dark, yellow highlight', preview: 'bg-neutral-900 text-white' },
  { id: 'minimal', name: 'Minimal', desc: 'Subtle, transparent background', preview: 'bg-neutral-600 text-white/80' },
  { id: 'colorful', name: 'Colorful', desc: 'Green highlight on dark', preview: 'bg-neutral-900 text-emerald-400' },
]

const SidebarPresetCard = memo(function SidebarPresetCard({ preset, onSelect, adding }: { preset: TextPresetConfig; onSelect: (p: TextPresetConfig) => void; adding: boolean }) {
  const isSmall = preset.fontSize <= 20
  const previewFontSize = isSmall ? 12 : Math.min(preset.fontSize * 0.28, 22)
  const subtitleFontSize = preset.subtitle ? Math.min(preset.subtitle.fontSize * 0.28, 11) : 0

  const textStyle: React.CSSProperties = {
    fontFamily: resolveFontCSS(preset.fontFamily),
    fontSize: previewFontSize,
    fontWeight: preset.fontWeight,
    color: preset.color,
    letterSpacing: preset.letterSpacing,
    textTransform: preset.textTransform as any,
    fontStyle: preset.fontStyle,
    lineHeight: 1.2,
  }

  if (preset.shadow) {
    textStyle.textShadow = preset.shadow
  }
  if (preset.glowColor) {
    textStyle.textShadow = `0 0 8px ${preset.glowColor}, 0 0 16px ${preset.glowColor}80`
  }

  const isGradient = preset.background?.match(/^(linear|radial)-gradient/)
  const hasBg = preset.background && !isGradient

  return (
    <button
      onClick={() => onSelect(preset)}
      disabled={adding}
      className={cn(
        'group relative w-full rounded-md overflow-hidden text-left transition-all duration-150',
        'border border-neutral-200 hover:border-neutral-400 hover:shadow-sm',
        'bg-neutral-800 disabled:opacity-50',
      )}
      style={{ minHeight: preset.exampleSubtitle ? 52 : 42 }}
    >
      <div className="flex items-center h-full px-2.5 py-2">
        {preset.accentBar && (
          <div
            className="absolute left-0 top-0 bottom-0"
            style={{ width: preset.accentBar.width * 1.5, backgroundColor: preset.accentBar.color }}
          />
        )}
        <div className={cn('flex-1 min-w-0', preset.accentBar && 'ml-2')}>
          {preset.lineAbove && <div className="w-6 h-px bg-neutral-400 mb-0.5" />}
          <div
            className="relative inline-block"
            style={{
              ...(hasBg ? { backgroundColor: preset.background, padding: '2px 6px', borderRadius: preset.borderRadius ?? 0 } : {}),
              ...(isGradient ? { background: preset.background, padding: '2px 6px', borderRadius: preset.borderRadius ?? 0 } : {}),
            }}
          >
            <span style={textStyle} className="block truncate">{preset.exampleText}</span>
          </div>
          {preset.exampleSubtitle && (
            <span
              className="block truncate mt-0.5"
              style={{
                fontFamily: resolveFontCSS(preset.subtitle?.fontFamily || preset.fontFamily),
                fontSize: subtitleFontSize,
                fontWeight: preset.subtitle?.fontWeight ?? 400,
                color: preset.subtitle?.color ?? '#a0a0a0',
                letterSpacing: preset.subtitle?.letterSpacing,
              }}
            >
              {preset.exampleSubtitle}
            </span>
          )}
          {preset.lineBelow && <div className="w-6 h-px bg-neutral-400 mt-0.5" />}
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-end pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-[9px] font-medium text-neutral-400 bg-neutral-700/90 px-1.5 py-0.5 rounded">
          {preset.name}
        </span>
      </div>
    </button>
  )
})

function TextPanel({ workflowId, currentTime }: { workflowId: Id<'workflows'>; currentTime: number }) {
  const addOverlay = useMutation(api.workflows.addTextOverlay)
  const [adding, setAdding] = useState(false)
  const [filter, setFilter] = useState<TextPresetCategory | 'all'>('all')

  const handleSelect = async (preset: TextPresetConfig) => {
    setAdding(true)
    try {
      const fields = presetToOverlayFields(preset)
      await addOverlay({
        workflowId,
        text: preset.exampleText,
        startTime: currentTime,
        duration: 4,
        ...fields,
        subtitleText: preset.exampleSubtitle,
      })
      toast({ title: `Added "${preset.name}"`, description: `At ${currentTime.toFixed(1)}s on timeline` })
    } catch (e) {
      toast({ title: 'Failed to add text', description: e instanceof Error ? e.message : 'Unknown error' })
    } finally {
      setAdding(false)
    }
  }

  const categories = Object.entries(PRESET_CATEGORY_LABELS) as [TextPresetCategory, string][]
  const grouped = filter === 'all'
    ? categories.map(([cat, label]) => ({
        category: cat,
        label,
        presets: TEXT_PRESETS.filter(p => p.category === cat),
      })).filter(g => g.presets.length > 0)
    : [{ category: filter, label: PRESET_CATEGORY_LABELS[filter], presets: TEXT_PRESETS.filter(p => p.category === filter) }]

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-1.5 text-[11px] text-muted-foreground">
        Click to add at playhead ({currentTime.toFixed(1)}s)
      </div>
      <div className="flex gap-1 px-2 py-1 border-b border-border shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
        <button
          onClick={() => setFilter('all')}
          className={cn(
            'px-2 py-0.5 text-[10px] font-medium rounded-sm whitespace-nowrap transition-colors',
            filter === 'all' ? 'bg-muted text-secondary-foreground' : 'text-muted-foreground hover:text-secondary-foreground hover:bg-muted',
          )}
        >
          All
        </button>
        {categories.map(([cat, label]) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={cn(
              'px-2 py-0.5 text-[10px] font-medium rounded-sm whitespace-nowrap transition-colors',
              filter === cat ? 'bg-muted text-secondary-foreground' : 'text-muted-foreground hover:text-secondary-foreground hover:bg-muted',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {grouped.map(({ category, label, presets }) => (
          <div key={category}>
            <h4 className="text-[10px] font-semibold text-muted-foreground tracking-widest uppercase mb-1.5 px-1">{label}</h4>
            <div className="space-y-1">
              {presets.map(preset => (
                <SidebarPresetCard key={preset.id} preset={preset} onSelect={handleSelect} adding={adding} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const POSITION_OPTIONS = [
  { value: 'center', label: 'Center' },
  { value: 'top_center', label: 'Top' },
  { value: 'bottom_center', label: 'Bottom' },
  { value: 'lower_third', label: 'Lower Third' },
  { value: 'upper_third', label: 'Upper Third' },
]

const ENTER_ANIMATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide_up', label: 'Slide Up' },
  { value: 'slide_down', label: 'Slide Down' },
  { value: 'slide_left', label: 'Slide Left' },
  { value: 'slide_right', label: 'Slide Right' },
  { value: 'scale', label: 'Scale' },
  { value: 'zoom_in', label: 'Zoom In' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'blur_in', label: 'Blur In' },
  { value: 'rotate_in', label: 'Rotate In' },
  { value: 'typewriter', label: 'Typewriter' },
  { value: 'letter_by_letter', label: 'Letter by Letter' },
  { value: 'glitch', label: 'Glitch' },
  { value: 'wave', label: 'Wave' },
]

const EXIT_ANIMATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide_up', label: 'Slide Up' },
  { value: 'slide_down', label: 'Slide Down' },
  { value: 'slide_left', label: 'Slide Left' },
  { value: 'slide_right', label: 'Slide Right' },
  { value: 'scale', label: 'Scale' },
  { value: 'zoom_out', label: 'Zoom Out' },
  { value: 'blur_out', label: 'Blur Out' },
  { value: 'rotate_out', label: 'Rotate Out' },
  { value: 'glitch', label: 'Glitch' },
]

const EMPHASIS_ANIMATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'breathe', label: 'Breathe' },
  { value: 'float', label: 'Float' },
  { value: 'glow_pulse', label: 'Glow Pulse' },
  { value: 'color_shift', label: 'Color Shift' },
]

const FONT_SIZE_OPTIONS = [16, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 80, 96]

function TextEditorPanel({ overlay, onUpdate, onBack }: {
  overlay: any
  onUpdate: (field: string, value: any) => void
  onBack: () => void
}) {
  const [localOverrides, setLocalOverrides] = useState<Record<string, any>>({})
  const prevOverlayId = useRef(overlay.id)

  useEffect(() => {
    if (overlay.id !== prevOverlayId.current) {
      setLocalOverrides({})
      prevOverlayId.current = overlay.id
    }
  }, [overlay.id])

  const merged = { ...overlay, ...localOverrides }

  const handleUpdate = useCallback((field: string, value: any) => {
    setLocalOverrides(prev => ({ ...prev, [field]: value }))
    onUpdate(field, value)
  }, [onUpdate])

  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const debouncedUpdate = useCallback((field: string, value: any) => {
    setLocalOverrides(prev => ({ ...prev, [field]: value }))
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => onUpdate(field, value), 200)
  }, [onUpdate])

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current) }, [])

  const fontFamily = merged.fontFamily || 'Inter'
  const fontSize = merged.fontSize || 48
  const fontWeight = merged.fontWeight || 400
  const color = merged.color || '#ffffff'
  const position = merged.position || 'center'
  const enterAnimation = merged.enterAnimation || merged.animation || 'fade'
  const exitAnimation = merged.exitAnimation || merged.animation || 'fade'
  const emphasisAnimation = merged.emphasisAnimation || 'none'
  const textTransform = merged.textTransform || 'none'
  const fontStyle = merged.fontStyle || 'normal'
  const background = merged.background || ''

  return (
    <div className="h-full flex flex-col">
      <button
        onClick={onBack}
        className="flex items-center gap-1 px-3 py-1.5 text-[11px] text-muted-foreground hover:text-secondary-foreground transition-colors shrink-0"
      >
        <ChevronLeft size={12} />
        Back to presets
      </button>

      {/* Live preview */}
      <div className="mx-3 mb-2 rounded-md bg-neutral-900 border border-border/60 overflow-hidden" style={{ minHeight: 48 }}>
        <div className="flex items-center justify-center p-3">
          <span
            className="text-center max-w-full break-words"
            style={{
              fontFamily: resolveFontCSS(fontFamily),
              fontSize: Math.min(fontSize * 0.35, 24),
              fontWeight,
              color,
              fontStyle,
              textTransform: textTransform as any,
              letterSpacing: merged.letterSpacing,
              ...(background ? { backgroundColor: background, padding: '2px 8px', borderRadius: merged.borderRadius ?? 0 } : {}),
              ...(merged.glowColor ? { textShadow: `0 0 8px ${merged.glowColor}, 0 0 16px ${merged.glowColor}80` } : {}),
            }}
          >
            {merged.text}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-4">
        {/* Text content */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Text</label>
          <textarea
            defaultValue={merged.text}
            onChange={(e) => debouncedUpdate('text', e.target.value)}
            rows={2}
            className="w-full bg-muted/50 border border-border rounded-md px-2 py-1.5 text-xs text-secondary-foreground resize-none focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
        </div>

        {/* Font family */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Font</label>
          <div className="grid grid-cols-2 gap-1">
            {PRESET_FONTS.map(f => (
              <button
                key={f}
                onClick={() => handleUpdate('fontFamily', f)}
                className={cn(
                  'px-2 py-1.5 text-[11px] rounded-md border transition-colors text-left truncate',
                  fontFamily === f
                    ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                    : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
                )}
                style={{ fontFamily: resolveFontCSS(f) }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Font size */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Size</label>
          <div className="flex flex-wrap gap-1">
            {FONT_SIZE_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => handleUpdate('fontSize', s)}
                className={cn(
                  'px-2 py-1 text-[10px] rounded-md border transition-colors',
                  fontSize === s
                    ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                    : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Style toggles: bold + italic */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Style</label>
          <div className="flex gap-1">
            <button
              onClick={() => handleUpdate('fontWeight', fontWeight >= 700 ? 400 : 700)}
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-md border transition-colors',
                fontWeight >= 700
                  ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                  : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
              )}
              title="Bold"
            >
              <Bold size={14} />
            </button>
            <button
              onClick={() => handleUpdate('fontStyle', fontStyle === 'italic' ? 'normal' : 'italic')}
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-md border transition-colors',
                fontStyle === 'italic'
                  ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                  : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
              )}
              title="Italic"
            >
              <Italic size={14} />
            </button>
            <button
              onClick={() => handleUpdate('textTransform', textTransform === 'uppercase' ? 'none' : 'uppercase')}
              className={cn(
                'px-2 h-8 text-[10px] font-semibold rounded-md border transition-colors',
                textTransform === 'uppercase'
                  ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                  : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
              )}
              title="Uppercase"
            >
              AA
            </button>
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => handleUpdate('color', e.target.value)}
              className="w-8 h-8 rounded-md border border-border cursor-pointer bg-transparent [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-0"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => {
                const v = e.target.value
                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) debouncedUpdate('color', v)
              }}
              className="flex-1 bg-muted/50 border border-border rounded-md px-2 py-1.5 text-xs text-secondary-foreground font-mono focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>
        </div>

        {/* Background */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Background</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={background || '#000000'}
              onChange={(e) => handleUpdate('background', e.target.value)}
              className="w-8 h-8 rounded-md border border-border cursor-pointer bg-transparent [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-0"
            />
            <input
              type="text"
              value={background}
              onChange={(e) => debouncedUpdate('background', e.target.value)}
              placeholder="none"
              className="flex-1 bg-muted/50 border border-border rounded-md px-2 py-1.5 text-xs text-secondary-foreground font-mono focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            {background && (
              <button onClick={() => handleUpdate('background', '')} className="text-[10px] text-muted-foreground hover:text-secondary-foreground">Clear</button>
            )}
          </div>
        </div>

        {/* Position */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Position</label>
          <div className="flex flex-wrap gap-1">
            {POSITION_OPTIONS.map(p => (
              <button
                key={p.value}
                onClick={() => handleUpdate('position', p.value)}
                className={cn(
                  'px-2 py-1 text-[10px] rounded-md border transition-colors',
                  position === p.value
                    ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                    : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Enter Animation */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Enter</label>
          <div className="flex flex-wrap gap-1">
            {ENTER_ANIMATION_OPTIONS.map(a => (
              <button
                key={a.value}
                onClick={() => handleUpdate('enterAnimation', a.value)}
                className={cn(
                  'px-2 py-1 text-[10px] rounded-md border transition-colors',
                  enterAnimation === a.value
                    ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                    : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
                )}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Emphasis Animation */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Emphasis</label>
          <div className="flex flex-wrap gap-1">
            {EMPHASIS_ANIMATION_OPTIONS.map(a => (
              <button
                key={a.value}
                onClick={() => handleUpdate('emphasisAnimation', a.value)}
                className={cn(
                  'px-2 py-1 text-[10px] rounded-md border transition-colors',
                  emphasisAnimation === a.value
                    ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                    : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
                )}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Exit Animation */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Exit</label>
          <div className="flex flex-wrap gap-1">
            {EXIT_ANIMATION_OPTIONS.map(a => (
              <button
                key={a.value}
                onClick={() => handleUpdate('exitAnimation', a.value)}
                className={cn(
                  'px-2 py-1 text-[10px] rounded-md border transition-colors',
                  exitAnimation === a.value
                    ? 'border-violet-500 bg-violet-500/10 text-secondary-foreground'
                    : 'border-border/60 text-muted-foreground hover:border-border hover:text-secondary-foreground',
                )}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom CSS */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Custom CSS</label>
          <textarea
            defaultValue={merged.customCSS || ''}
            onChange={(e) => debouncedUpdate('customCSS', e.target.value)}
            rows={2}
            placeholder="background: linear-gradient(...);"
            className="w-full bg-muted/50 border border-border rounded-md px-2 py-1.5 text-[10px] font-mono text-secondary-foreground resize-none focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
        </div>

        {/* Apply preset */}
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider block mb-1">Apply Preset</label>
          <div className="space-y-1">
            {TEXT_PRESETS.slice(0, 8).map(preset => (
              <SidebarPresetCard key={preset.id} preset={preset} onSelect={(p) => {
                const fields = presetToOverlayFields(p)
                for (const [k, v] of Object.entries(fields)) {
                  if (v !== undefined) handleUpdate(k, v)
                }
              }} adding={false} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function TransitionsPanel({ clipId, clips, selectedClipIndex }: { clipId: string | null; clips: any[]; selectedClipIndex: number | null }) {
  const targetClip = clipId
    ? clips.find((c: any) => c._id === clipId)
    : selectedClipIndex !== null ? clips[selectedClipIndex] : null
  const targetClipIndex = targetClip ? clips.indexOf(targetClip) : null

  const currentTransition = targetClip?.transition
  const [selectedType, setSelectedType] = useState<string | null>(currentTransition?.type ?? null)
  const [duration, setDuration] = useState(currentTransition?.duration ?? 0.5)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)
  const updateTransition = useMutation(api.workflows.updateClipTransition)
  const durationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selectedTypeRef = useRef<string | null>(selectedType)

  useEffect(() => {
    const t = targetClip?.transition
    setSelectedType(t?.type ?? null)
    setDuration(t?.duration ?? 0.5)
  }, [targetClip?._id, targetClip?.transition?.type, targetClip?.transition?.duration])

  useEffect(() => {
    selectedTypeRef.current = selectedType
  }, [selectedType])

  useEffect(() => {
    return () => {
      if (durationTimerRef.current) clearTimeout(durationTimerRef.current)
    }
  }, [])

  const handleSelect = useCallback(async (type: string) => {
    if (isPending || !targetClip) return
    setSelectedType(type)
    setIsPending(true)
    try {
      await updateTransition({
        clipId: targetClip._id as Id<"workflowClips">,
        type,
        duration,
      })
    } finally {
      setIsPending(false)
    }
  }, [targetClip, duration, updateTransition, isPending])

  const handleDurationChange = useCallback((newDuration: number) => {
    setDuration(newDuration)
    if (durationTimerRef.current) clearTimeout(durationTimerRef.current)
    durationTimerRef.current = setTimeout(() => {
      const currentType = selectedTypeRef.current
      if (currentType && targetClip) {
        updateTransition({
          clipId: targetClip._id as Id<"workflowClips">,
          type: currentType,
          duration: newDuration,
        })
      }
    }, 300)
  }, [targetClip, updateTransition])

  const handleRemove = useCallback(async () => {
    if (isPending || !targetClip) return
    setSelectedType(null)
    setIsPending(true)
    try {
      await updateTransition({
        clipId: targetClip._id as Id<"workflowClips">,
        type: 'cut',
      })
    } finally {
      setIsPending(false)
    }
  }, [targetClip, updateTransition, isPending])

  if (!targetClip) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-4 text-center">
        <Blend size={24} className="text-muted-foreground/50 mb-2" />
        <p className="text-[11px] text-muted-foreground">Select a clip to apply a transition.</p>
        <p className="text-[10px] text-muted-foreground mt-1">Click a transition indicator between clips, or select a clip on the timeline.</p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 text-[11px] text-muted-foreground border-b border-border">
        Transition into{' '}
        <span className="font-medium text-secondary-foreground">
          Clip {(targetClipIndex ?? 0) + 1}
        </span>
        {targetClip.title ? `: ${targetClip.title}` : ''}
      </div>

      <div className="px-3 py-2 border-b border-border space-y-2">
        <PillSlider
          label="Duration"
          value={duration}
          onChange={handleDurationChange}
          min={0.1}
          max={2.0}
          step={0.1}
          unit="s"
          displayMultiplier={1}
          displayPrecision={1}
          disabled={!selectedType}
          ticks={5}
        />
        {selectedType && (
          <button
            onClick={handleRemove}
            disabled={isPending}
            className="flex items-center gap-1.5 text-[11px] text-destructive hover:text-destructive/80 py-0.5 disabled:opacity-50"
          >
            <Trash2 size={11} />
            Remove transition
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-2 pt-2 pb-1">
          <button
            onClick={handleRemove}
            disabled={isPending}
            className={cn(
              'w-full flex items-center gap-2 p-2 rounded-md border transition-all text-left',
              !selectedType
                ? 'border-border bg-secondary'
                : 'border-dashed border-border hover:border-border hover:bg-secondary',
              'disabled:opacity-50'
            )}
          >
            <div className="w-12 h-7 rounded-sm bg-muted border border-border flex items-center justify-center">
              <span className="text-[9px] text-muted-foreground font-mono">CUT</span>
            </div>
            <div>
              <span className={cn(
                'text-[11px] font-medium',
                !selectedType ? 'text-secondary-foreground' : 'text-muted-foreground'
              )}>None</span>
              <span className="text-[9px] text-muted-foreground ml-1.5">Hard cut</span>
            </div>
          </button>
        </div>

        {TRANSITION_CATEGORIES.map((category) => {
          const isExpanded = expandedCategory === null || expandedCategory === category.label
          const hasActive = category.transitions.some(t => t.type === selectedType)

          return (
            <div key={category.label}>
              <button
                onClick={() => setExpandedCategory(prev => prev === category.label ? null : category.label)}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold tracking-wider uppercase',
                  hasActive ? 'text-primary' : 'text-muted-foreground',
                  'hover:bg-accent'
                )}
              >
                {category.label}
                <ChevronRight size={12} className={cn(
                  'text-muted-foreground transition-transform duration-150',
                  isExpanded && 'rotate-90'
                )} />
              </button>
              {isExpanded && (
                <div className="grid grid-cols-3 gap-1 px-2 pb-2">
                  {category.transitions.map((t) => {
                    const isActive = selectedType === t.type
                    return (
                      <button
                        key={t.type}
                        onClick={() => handleSelect(t.type)}
                        disabled={isPending}
                        className={cn(
                          'flex flex-col items-center gap-1 p-1.5 rounded-md border transition-all',
                          isActive
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-transparent hover:border-border hover:bg-secondary',
                          'disabled:opacity-50'
                        )}
                      >
                        <TransitionPreviewCanvas type={t.type} isActive={isActive} />
                        <span className={cn(
                          'text-[10px] leading-tight text-center',
                          isActive ? 'text-primary font-medium' : 'text-muted-foreground'
                        )}>
                          {t.name}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CaptionsPanel({ workflowId, textOverlays }: { workflowId: Id<'workflows'>; textOverlays?: any[] }) {
  const generateCaptions = useMutation(api.workflows.generateCaptions)
  const removeOverlay = useMutation(api.workflows.removeTextOverlay)
  const [generating, setGenerating] = useState(false)
  const [selectedStyle, setSelectedStyle] = useState('bold')

  const captionCount = (textOverlays || []).filter((o: any) => (o.id as string).startsWith('caption-')).length

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const result = await generateCaptions({ workflowId, style: selectedStyle })
      toast({
        title: `Generated ${result.captionsGenerated} captions`,
        description: `From ${result.clipsProcessed} clips`,
      })
    } catch (e) {
      toast({ title: 'Caption generation failed', description: e instanceof Error ? e.message : 'Unknown error' })
    } finally {
      setGenerating(false)
    }
  }

  const handleClearCaptions = async () => {
    const captions = (textOverlays || []).filter((o: any) => (o.id as string).startsWith('caption-'))
    for (const c of captions) {
      await removeOverlay({ workflowId, overlayId: c.id })
    }
    toast({ title: `Removed ${captions.length} captions` })
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 text-[11px] text-muted-foreground">
        {captionCount > 0
          ? `${captionCount} caption overlays on timeline.`
          : 'Generate captions from clip transcriptions.'
        }
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-3">
        <button
          onClick={handleGenerate}
          disabled={generating}
          className={cn(
            'w-full px-3 py-3 rounded-md border-2 border-dashed transition-all flex flex-col items-center gap-1.5',
            generating
              ? 'border-border bg-secondary'
              : 'border-border hover:border-muted-foreground bg-background hover:bg-secondary'
          )}
        >
          {generating ? (
            <Loader2 size={16} className="text-muted-foreground animate-spin" />
          ) : (
            <Captions size={16} className="text-muted-foreground" />
          )}
          <span className="text-[11px] font-medium text-secondary-foreground">
            {generating ? 'Generating...' : captionCount > 0 ? 'Regenerate captions' : 'Auto-generate captions'}
          </span>
          <span className="text-[10px] text-muted-foreground">From clip transcriptions</span>
        </button>

        {captionCount > 0 && (
          <button
            onClick={handleClearCaptions}
            className="w-full text-[11px] text-destructive hover:text-destructive/80 py-1"
          >
            Clear all captions ({captionCount})
          </button>
        )}

        {captionCount > 0 && (
          <CaptionStylePanel workflowId={workflowId} textOverlays={textOverlays} />
        )}

        {captionCount === 0 && (
          <div className="border-t border-border pt-2">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1 mb-1.5">Style</p>
            {CAPTION_STYLES.map(style => (
              <button
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2',
                  selectedStyle === style.id ? 'bg-muted' : 'hover:bg-accent'
                )}
              >
                <div className={cn('w-5 h-3 rounded-sm text-[6px] flex items-center justify-center font-bold', style.preview)}>
                  Aa
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] text-secondary-foreground font-medium">{style.name}</span>
                  <p className="text-[9px] text-muted-foreground">{style.desc}</p>
                </div>
                {selectedStyle === style.id && <Check size={12} className="text-muted-foreground shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


function CollapsibleSection({ title, defaultOpen = true, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-secondary-foreground transition-colors"
      >
        <ChevronRight size={12} className={cn('transition-transform', open && 'rotate-90')} />
        {title}
      </button>
      {open && <div className="px-2 pb-3">{children}</div>}
    </div>
  )
}

function EffectsPanel({ clips, selectedClipIndex, workflowId, currentTime, timelineMode }: { clips: any[]; selectedClipIndex: number | null; workflowId: Id<'workflows'>; currentTime: number; timelineMode?: TimelineMode }) {
  const selectedClip = selectedClipIndex != null ? clips[selectedClipIndex] : null
  const updateKeyframes = useMutation(api.workflows.updateClipAnimationKeyframes)
  const [curveEditorProperty, setCurveEditorProperty] = useState<string | undefined>()

  const clipLocalTime = useMemo(() => {
    if (!selectedClip || selectedClipIndex == null) return 0
    const model = buildTimelineModel(clips, { mode: timelineMode })
    const entry = model.entries[selectedClipIndex]
    if (!entry) return 0
    return Math.max(0, currentTime - entry.globalStart)
  }, [selectedClip, selectedClipIndex, clips, currentTime, timelineMode])

  const animationKeyframes = useMemo(
    () => selectedClip?.effects?.animationKeyframes ?? { tracks: [] },
    [selectedClip?.effects?.animationKeyframes],
  )

  const handleKeyframeToggle = useCallback((property: string, time: number, value: number) => {
    if (!selectedClip) return
    const existing = getKfAtTime(animationKeyframes, property, time)
    const updated = existing
      ? removeKf(animationKeyframes, property, time)
      : addKf(animationKeyframes, property, time, value)
    updateKeyframes({
      clipId: selectedClip._id as Id<"workflowClips">,
      animationKeyframes: updated,
    })
  }, [selectedClip, animationKeyframes, updateKeyframes])

  const handleUpdateKeyframe = useCallback((property: string, index: number, updates: any) => {
    if (!selectedClip) return
    const updated = updateKf(animationKeyframes, property, index, updates)
    updateKeyframes({
      clipId: selectedClip._id as Id<"workflowClips">,
      animationKeyframes: updated,
    })
  }, [selectedClip, animationKeyframes, updateKeyframes])

  if (!selectedClip) {
    return (
      <div className="h-full flex items-center justify-center px-4">
        <p className="text-xs text-muted-foreground text-center">Select a clip to edit effects</p>
      </div>
    )
  }

  const hasAnyKeyframes = animationKeyframes.tracks.length > 0 &&
    animationKeyframes.tracks.some((t: any) => t.keyframes.length > 0)
  const clipDuration = (selectedClip.duration - (selectedClip.trimStart || 0) - (selectedClip.trimEnd || 0)) / (selectedClip.speed || 1)

  return (
    <div className="h-full overflow-y-auto">
      <CollapsibleSection title="Color & Effects">
        <FiltersPanel
          clipId={selectedClip._id}
          effects={selectedClip.effects}
          thumbnailUrl={selectedClip.firstFrameUrl || selectedClip.storyboardImageUrl || selectedClip.startKeyframeUrl}
          onClose={() => {}}
          embedded
          animationKeyframes={animationKeyframes}
          currentClipTime={clipLocalTime}
          onKeyframeToggle={handleKeyframeToggle}
        />
      </CollapsibleSection>
      {hasAnyKeyframes && (
        <>
          <div className="h-px bg-border mx-3" />
          <CollapsibleSection title="Keyframe Curves" defaultOpen>
            <div className="h-[200px]">
              <KeyframeCurveEditor
                tracks={animationKeyframes}
                clipDuration={clipDuration}
                currentTime={clipLocalTime}
                onUpdateKeyframe={handleUpdateKeyframe}
                selectedProperty={curveEditorProperty}
                onSelectProperty={setCurveEditorProperty}
              />
            </div>
          </CollapsibleSection>
        </>
      )}
      <div className="h-px bg-border mx-3" />
      <CollapsibleSection title="Camera Motion">
        <MotionPresetPicker
          clipId={selectedClip._id}
          kenBurns={selectedClip.effects?.kenBurns}
          speed={selectedClip.speed}
          allClipIds={clips.map((c: any) => c._id)}
          workflowId={workflowId as string}
        />
      </CollapsibleSection>
    </div>
  )
}

export function RightSidebar({ workflowId, messages, clips, characters, isAdmin, selectedClipIndex, currentTime, textOverlays, selectedOverlayId, onOverlaySelect, onOverlayUpdate, transitionPickerClipId, onTransitionPickerChange, layoutActiveTab, projectTtsStatus, projectTtsTimestamps, timelineMode }: RightSidebarProps) {
  const activeSkillNames = useQuery(api.skills.getActiveSkillNames) ?? []
  const tabs = useMemo(() => getVisibleTabs(activeSkillNames), [activeSkillNames])
  const tabIds = useMemo(() => new Set(tabs.map(t => t.id)), [tabs])
  const [activeTab, setActiveTab] = useState<RightSidebarTab | null>(null)
  const [width, setWidth] = useState(280)
  const prevLayoutTab = useRef(layoutActiveTab)

  useEffect(() => {
    if (activeTab && !tabIds.has(activeTab)) setActiveTab(null)
  }, [activeTab, tabIds])

  useEffect(() => {
    if (layoutActiveTab && layoutActiveTab !== prevLayoutTab.current && tabIds.has(layoutActiveTab as RightSidebarTab)) {
      setActiveTab(layoutActiveTab as RightSidebarTab)
    }
    prevLayoutTab.current = layoutActiveTab
  }, [layoutActiveTab, tabIds])

  useEffect(() => {
    if (transitionPickerClipId) {
      setActiveTab('transitions')
    }
  }, [transitionPickerClipId])

  useEffect(() => {
    if (selectedOverlayId) {
      setActiveTab('text')
    }
  }, [selectedOverlayId])


  const isDragging = useRef(false)
  const startX = useRef(0)
  const startWidth = useRef(280)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true
    startX.current = e.clientX
    startWidth.current = width
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    e.preventDefault()
  }, [width])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return
      const delta = startX.current - e.clientX
      const newWidth = Math.min(1000, Math.max(100, startWidth.current + delta))
      setWidth(newWidth)
    }
    const handleMouseUp = () => {
      if (!isDragging.current) return
      isDragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const handleTabClick = (tabId: RightSidebarTab) => {
    setActiveTab(prev => {
      if (prev === tabId && tabId === 'text' && selectedOverlayId) {
        return prev
      }
      const next = prev === tabId ? null : tabId
      if (prev === 'text' && next !== 'text' && selectedOverlayId) {
        onOverlaySelect?.(null)
      }
      if (next !== 'transitions' && transitionPickerClipId) {
        onTransitionPickerChange?.(null)
      }
      return next
    })
  }

  const isOpen = activeTab !== null

  return (
    <div className="flex h-full">
      {isOpen && (
        <>
        <div
          onMouseDown={handleMouseDown}
          className="w-[2px] shrink-0 bg-border/50 hover:bg-primary/30 cursor-col-resize transition-colors relative group"
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
        </div>
        <div style={{ width }} className="h-full bg-card overflow-hidden flex flex-col">
          {activeTab === 'music' ? (
            <MusicPicker
              workflowId={workflowId}
              onClose={() => setActiveTab(null)}
              totalDuration={buildTimelineModel(clips, { mode: timelineMode }).totalDuration}
            />
          ) : (
            <>
              <div className="px-3 py-1.5 border-b border-border/60 shrink-0">
                {activeTab === 'text' && selectedOverlayId && (textOverlays || []).find((o: any) => o.id === selectedOverlayId) ? (
                  <h3 className="text-[11px] font-medium text-secondary-foreground truncate">
                    Editing: <span className="text-muted-foreground">{(textOverlays || []).find((o: any) => o.id === selectedOverlayId)?.text}</span>
                  </h3>
                ) : (
                  <h3 className="text-[11px] font-medium text-muted-foreground">
                    {tabs.find(t => t.id === activeTab)?.label}
                  </h3>
                )}
              </div>
              <div className="flex-1 min-h-0 overflow-hidden">
                {activeTab === 'script' && (
                  <ScriptPanel clips={clips} characters={characters || []} projectTtsStatus={projectTtsStatus} projectTtsTimestamps={projectTtsTimestamps} workflowId={workflowId} />
                )}
                {activeTab === 'media' && <AssetLibraryPanel workflowId={workflowId} />}
                {activeTab === 'activity' && (
                  <ActivityTimeline messages={messages} clips={clips} workflowId={workflowId} isAdmin={isAdmin} />
                )}
                {activeTab === 'text' && (() => {
                  const selectedOverlay = selectedOverlayId ? (textOverlays || []).find((o: any) => o.id === selectedOverlayId) : null
                  if (selectedOverlay && onOverlayUpdate) {
                    return <TextEditorPanel overlay={selectedOverlay} onUpdate={(field, value) => onOverlayUpdate(selectedOverlay.id, { [field]: value })} onBack={() => onOverlaySelect?.(null)} />
                  }
                  return <TextPanel workflowId={workflowId} currentTime={currentTime} />
                })()}
                {activeTab === 'transitions' && <TransitionsPanel clipId={transitionPickerClipId ?? null} clips={clips} selectedClipIndex={selectedClipIndex} />}
                {activeTab === 'captions' && <CaptionsPanel workflowId={workflowId} textOverlays={textOverlays} />}
                {activeTab === 'effects' && <EffectsPanel clips={clips} selectedClipIndex={selectedClipIndex} workflowId={workflowId} currentTime={currentTime} timelineMode={timelineMode} />}
              </div>
            </>
          )}
        </div>
        </>
      )}

      <div className="w-10 shrink-0 bg-card border-l border-border/60 flex flex-col items-center pt-2 gap-0.5">
        {tabs.map(({ id, icon: Icon, label }) => {
          const selectedClip = selectedClipIndex != null ? clips[selectedClipIndex] : null
          const hasSelectedOverlay = id === 'text' && !!selectedOverlayId
          const hasActiveEffects = id === 'effects' && selectedClip?.effects && (
            (selectedClip.effects.brightness != null && selectedClip.effects.brightness !== 1) ||
            (selectedClip.effects.contrast != null && selectedClip.effects.contrast !== 1) ||
            (selectedClip.effects.saturation != null && selectedClip.effects.saturation !== 1) ||
            (selectedClip.effects.hueRotate != null && selectedClip.effects.hueRotate !== 0) ||
            selectedClip.effects.colorGrade ||
            (selectedClip.effects.vignette != null && selectedClip.effects.vignette > 0) ||
            (selectedClip.effects.blur != null && selectedClip.effects.blur > 0) ||
            (selectedClip.effects.bloom != null && selectedClip.effects.bloom > 0) ||
            selectedClip.effects.letterbox
          )

          return (
            <button
              key={id}
              onClick={() => handleTabClick(id)}
              className={cn(
                'relative w-8 h-8 rounded-md flex items-center justify-center transition-colors',
                activeTab === id
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-secondary-foreground hover:bg-muted'
              )}
              title={label}
            >
              <Icon size={15} />
              {(hasActiveEffects || hasSelectedOverlay) && (
                <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
